import React from "react";

export const BAA_VERSION = "1.0.0";
export const BAA_EFFECTIVE_DATE = "2025-01-01";

export interface BAASection {
  heading: string;
  paragraphs: string[];
}

/**
 * Single source of truth for the BAA text. Both the on-screen review panel
 * and the generated signed PDF render from this same data, so the document
 * the user reads is byte-for-byte the document they sign.
 */
export const BAA_SECTIONS: BAASection[] = [
  {
    heading: "1. Definitions",
    paragraphs: [
      "Capitalized terms used in this Business Associate Agreement (\"Agreement\") and not otherwise defined shall have the meanings set forth in the HIPAA Regulations at 45 CFR Parts 160 and 164, as amended from time to time.",
      "\"Covered Entity\" means the home care agency, group home, or other healthcare organization (\"Customer\") that has entered into a service agreement with H&E Associates LLC d/b/a LegacyGrowth Engine (\"Business Associate\").",
      "\"Protected Health Information\" or \"PHI\" has the meaning given to such term under 45 CFR § 160.103, limited to the information created, received, maintained, or transmitted by Business Associate from or on behalf of Covered Entity.",
    ],
  },
  {
    heading: "2. Permitted Uses and Disclosures of PHI",
    paragraphs: [
      "Business Associate may use or disclose PHI only as necessary to perform the services set forth in the underlying service agreement, as Required by Law, or as otherwise permitted under this Agreement.",
      "Business Associate may use PHI for its proper management and administration, to carry out its legal responsibilities, and for data aggregation services as permitted under 45 CFR § 164.504(e)(2)(i)(B).",
      "Business Associate shall not use or disclose PHI in a manner that would violate Subpart E of 45 CFR Part 164 if done by Covered Entity.",
    ],
  },
  {
    heading: "3. Obligations of Business Associate",
    paragraphs: [
      "Business Associate agrees to: (a) not use or disclose PHI other than as permitted or required by this Agreement or as Required by Law; (b) use appropriate safeguards, and comply with Subpart C of 45 CFR Part 164 with respect to electronic PHI, to prevent use or disclosure of PHI other than as provided for by this Agreement; (c) report to Covered Entity any use or disclosure of PHI not provided for by this Agreement of which it becomes aware, including breaches of unsecured PHI as required by 45 CFR § 164.410; (d) ensure that any subcontractors that create, receive, maintain, or transmit PHI on behalf of Business Associate agree to the same restrictions and conditions that apply to Business Associate; (e) make available PHI in a designated record set as necessary to satisfy Covered Entity's obligations under 45 CFR § 164.524; (f) make any amendment(s) to PHI in a designated record set as directed or agreed to by Covered Entity pursuant to 45 CFR § 164.526; (g) maintain and make available the information required to provide an accounting of disclosures as necessary to satisfy Covered Entity's obligations under 45 CFR § 164.528.",
    ],
  },
  {
    heading: "4. Safeguards",
    paragraphs: [
      "Business Associate shall implement administrative, physical, and technical safeguards that reasonably and appropriately protect the confidentiality, integrity, and availability of electronic PHI, including without limitation: (i) encryption of PHI at rest using AES-256 or equivalent; (ii) encryption of PHI in transit using TLS 1.2 or higher; (iii) role-based access controls and least-privilege provisioning; (iv) audit logging of all access to PHI; (v) workforce training on HIPAA Privacy and Security Rules; and (vi) periodic risk assessments.",
    ],
  },
  {
    heading: "5. Breach Notification",
    paragraphs: [
      "Business Associate shall notify Covered Entity of any Breach of Unsecured PHI without unreasonable delay and in no case later than sixty (60) calendar days after discovery. Such notification shall include, to the extent possible, the identification of each individual whose Unsecured PHI has been, or is reasonably believed to have been, accessed, acquired, used, or disclosed during the Breach, along with any other information required by 45 CFR § 164.410.",
    ],
  },
  {
    heading: "6. Term and Termination",
    paragraphs: [
      "This Agreement shall be effective as of the date of electronic signature and shall terminate when all PHI provided by Covered Entity to Business Associate, or created, maintained, or received by Business Associate on behalf of Covered Entity, is destroyed or returned to Covered Entity, or, if it is infeasible to return or destroy PHI, protections are extended to such information in accordance with the termination provisions of this Section.",
      "Covered Entity may terminate this Agreement immediately upon written notice if Business Associate breaches a material term and fails to cure such breach within thirty (30) days of receipt of notice.",
      "Upon termination, Business Associate shall return or destroy all PHI received from Covered Entity, or created, maintained, or received by Business Associate on behalf of Covered Entity. Where return or destruction is infeasible, Business Associate shall extend the protections of this Agreement to the PHI and limit further uses and disclosures to those purposes that make the return or destruction infeasible.",
    ],
  },
  {
    heading: "7. Miscellaneous",
    paragraphs: [
      "Regulatory References. A reference in this Agreement to a section in the HIPAA Regulations means the section as in effect or as amended.",
      "Amendment. The Parties agree to take such action as is necessary to amend this Agreement from time to time as is necessary for compliance with the requirements of HIPAA, the HITECH Act, and any other applicable law.",
      "Survival. The respective rights and obligations of Business Associate under Section 6 of this Agreement shall survive the termination of this Agreement.",
      "Interpretation. Any ambiguity in this Agreement shall be resolved to permit the Parties to comply with HIPAA and the HITECH Act.",
      "Electronic Signature. The Parties agree that this Agreement may be executed by electronic signature, which shall be considered the legal equivalent of a handwritten signature pursuant to the Electronic Signatures in Global and National Commerce Act (E-SIGN) and the Uniform Electronic Transactions Act (UETA).",
    ],
  },
];

/** Plain-text rendering used to compute the document hash and feed the PDF. */
export function getBAAPlainText(): string {
  const header = `BUSINESS ASSOCIATE AGREEMENT\nVersion ${BAA_VERSION} — Effective ${BAA_EFFECTIVE_DATE}\n\n`;
  const intro = "This Business Associate Agreement is entered into between H&E Associates LLC d/b/a LegacyGrowth Engine (\"Business Associate\") and the undersigned Covered Entity, pursuant to the Health Insurance Portability and Accountability Act of 1996, as amended by the Health Information Technology for Economic and Clinical Health Act (HITECH), and the regulations promulgated thereunder.\n\n";
  const body = BAA_SECTIONS.map(s =>
    `${s.heading}\n\n${s.paragraphs.join("\n\n")}\n`
  ).join("\n");
  return header + intro + body;
}

/** SHA-256 hex of the plain-text BAA — what the user signs. */
export async function computeBAADocumentHash(): Promise<string> {
  const text = getBAAPlainText();
  const buf = new TextEncoder().encode(text);
  const hashBuf = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hashBuf))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
}

/** JSX rendering used by BAAReviewPanel — content is identical to plain text. */
export function BAADocumentBody() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-foreground">
      <header className="space-y-1">
        <h1 className="text-xl font-bold">Business Associate Agreement</h1>
        <p className="text-xs text-muted-foreground">
          Version {BAA_VERSION} — Effective {BAA_EFFECTIVE_DATE}
        </p>
      </header>
      <p>
        This Business Associate Agreement is entered into between
        <strong> H&E Associates LLC d/b/a LegacyGrowth Engine </strong>
        (&ldquo;Business Associate&rdquo;) and the undersigned Covered Entity,
        pursuant to the Health Insurance Portability and Accountability Act of
        1996, as amended by the Health Information Technology for Economic and
        Clinical Health Act (HITECH), and the regulations promulgated
        thereunder.
      </p>
      {BAA_SECTIONS.map(section => (
        <section key={section.heading} className="space-y-2">
          <h2 className="text-base font-semibold">{section.heading}</h2>
          {section.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </section>
      ))}
    </div>
  );
}